import React from 'react';
import { Clock, Calendar, MapPin, Users, Music, Utensils, Heart, Star, Gift, Camera } from 'lucide-react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

const ICON_MAP = {
  clock: Clock, calendar: Calendar, 'map-pin': MapPin, users: Users,
  music: Music, utensils: Utensils, heart: Heart, star: Star,
  gift: Gift, camera: Camera,
};

export default function DetailsGrid({ section, data, theme }) {
  const anim = section.animation || {};
  const items = data.items || [];
  const cs = data.customStyle || {};

  if (items.length === 0 && !data.date && !data.venue) return null;

  const isDark = isColorDark(cs.bgColor || theme.bg);
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const innerBg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
  const borderC = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <section
      className="px-5 py-20"
      style={{ background: cs.bgColor || cardBg }}
      data-testid="event-details-section"
    >
      <div className="max-w-sm mx-auto">
        {data.title && (
          <AnimatedSection preset={anim.items || 'fade-up'}>
            <p
              className="text-xs tracking-[0.3em] uppercase mb-2 text-center"
              style={{
                color: cs.accentColor || theme.accent,
                fontFamily: cs.fontFamily || theme.eyebrowFont,
              }}
            >
              {data.title}
            </p>
          </AnimatedSection>
        )}
        {data.subtitle && (
          <AnimatedSection preset={anim.items || 'fade-up'}>
            <p
              className="text-sm text-center mb-8"
              style={{
                color: cs.textColor || theme.textSecondary,
                fontFamily: cs.fontFamily || theme.bodyFont,
              }}
            >
              {data.subtitle}
            </p>
          </AnimatedSection>
        )}
        <div className="space-y-4">
          {items.map((item, i) => {
            const IconComp = ICON_MAP[item.icon] || Clock;
            return (
              <AnimatedSection key={i} preset={anim.items || 'stagger-cards'} delay={i * 0.1}>
                <div
                  className="rounded-2xl p-5"
                  style={{ background: innerBg, border: `1px solid ${borderC}` }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${cs.accentColor || theme.accent}15` }}
                    >
                      <IconComp className="w-4 h-4" style={{ color: cs.accentColor || theme.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-medium text-sm mb-1"
                        style={{
                          color: cs.textColor || theme.textPrimary,
                          fontFamily: cs.fontFamily || theme.bodyFont,
                        }}
                      >
                        {item.label || item.title || 'Detail'}
                      </h3>
                      {(item.value || item.time) && (
                        <p className="text-xs mb-1 flex items-center gap-1.5" style={{
                          color: cs.textColor ? `${cs.textColor}cc` : theme.textSecondary,
                          fontFamily: cs.fontFamily || theme.eyebrowFont,
                        }}>
                          {item.value || item.time}
                        </p>
                      )}
                      {item.description && (
                        <p className="text-xs leading-relaxed" style={{ color: cs.textColor ? `${cs.textColor}bb` : theme.textSecondary }}>
                          {item.description}
                        </p>
                      )}
                      {item.mapUrl && (
                        <a
                          href={item.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-medium mt-2 hover:underline"
                          style={{ color: cs.accentColor || theme.accent }}
                          data-testid={`detail-map-link-${i}`}
                        >
                          <MapPin className="w-3 h-3" /> Open Map
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function isColorDark(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
